<template>
  <div class="salesman-page">
    <header class="org-banner" :class="{ invalid: orgId && !orgValid }">
      <template v-if="orgId">
        <p class="org-banner-label">所属机构</p>
        <p v-if="loadingOrg" class="org-banner-text">机构信息加载中…</p>
        <template v-else-if="orgInfo">
          <p class="org-banner-name">{{ orgInfo.orgName }}</p>
          <p class="org-banner-id">机构ID：{{ orgInfo.orgId }}</p>
        </template>
        <template v-else>
          <p class="org-banner-name">机构id不正确</p>
          <p class="org-banner-id">该机构不存在或已被禁用，请联系管理员</p>
        </template>
      </template>
      <template v-else>
        <p class="org-banner-name">业务员登录</p>
        <p class="org-banner-id">{{ taikangMode ? '请通过带有机构信息的业务员链接进入' : '注册需通过机构分享的链接进入，登录无需机构信息' }}</p>
      </template>
    </header>

    <main class="form-card">
      <van-tabs
        v-model="activeTab"
        :swipeable="false"
        :animated="true"
        :color="'#168de0'"
        :title-active-color="'#168de0'"
        :title-inactive-color="'#7c8d9c'"
        :line-width="24"
        :line-height="3"
      >
        <van-tab :disabled="!orgId" title="注册">
          <van-field
            v-model="registerForm.salesmanName"
            label="姓名"
            placeholder="请输入姓名"
            maxlength="20"
            clearable
          />
          <van-field
            v-model="registerForm.salesmanCode"
            label="业务员编码"
            placeholder="请输入业务员编码"
            maxlength="32"
            clearable
            :class="{ 'field-error': salesmanCodeInvalid }"
          />
          <p class="field-count-tip" role="status" aria-live="polite">已输入 {{ registerForm.salesmanCode.length }} 位</p>
          <p v-if="salesmanCodeInvalid" class="field-error-tip">请输入非空业务员编码，最多32位</p>
          <van-field
            v-model="registerForm.userPhone"
            type="tel"
            label="手机号"
            placeholder="请输入手机号"
            maxlength="11"
            clearable
          />
          <van-field
            v-if="!taikangMode"
            v-model="registerForm.securityCode"
            label="验证码"
            placeholder="请输入验证码"
            maxlength="6"
            clearable
          >
            <template #button>
              <button
                type="button"
                class="code-button"
                :disabled="!canGetCode"
                @click="handleGetCode"
              >
                {{ codeButtonText }}
              </button>
            </template>
          </van-field>

          <button
            type="button"
            class="submit-button"
            :disabled="!canRegister || submitting"
            @click="handleRegister"
          >
            {{ submitting ? "注册中..." : "注册" }}
          </button>
          <p class="switch-tip">
            已有账号？<a @click="switchToLogin">去登录</a>
          </p>
        </van-tab>

        <van-tab title="登录">
          <van-field
            v-model="loginForm.userPhone"
            type="tel"
            label="手机号"
            placeholder="请输入手机号"
            maxlength="11"
            clearable
          />
          <van-field
            v-if="taikangMode"
            v-model="loginForm.salesmanCode"
            label="业务员编码"
            placeholder="请输入业务员编码"
            maxlength="32"
            clearable
            @keyup.enter="handleLogin"
          />
          <p v-if="taikangMode" class="field-count-tip" role="status" aria-live="polite">已输入 {{ loginForm.salesmanCode.length }} 位</p>
          <van-field
            v-if="!taikangMode"
            v-model="loginForm.securityCode"
            label="验证码"
            placeholder="请输入验证码"
            maxlength="6"
            clearable
          >
            <template #button>
              <button
                type="button"
                class="code-button"
                :disabled="!canGetLoginCode"
                @click="handleGetLoginCode"
              >
                {{ loginCodeButtonText }}
              </button>
            </template>
          </van-field>

          <button
            type="button"
            class="submit-button"
            :disabled="!canLogin || submitting"
            @click="handleLogin"
          >
            {{ submitting ? "登录中..." : "登录" }}
          </button>
          <p v-if="orgId" class="switch-tip">
            还没有账号？<a @click="switchToRegister">去注册</a>
          </p>
          <p v-else class="switch-tip">
            注册需通过机构分享的链接进入
          </p>
        </van-tab>
      </van-tabs>
    </main>

    <van-overlay :show="submitting" z-index="2000">
      <van-loading color="#fff" size="36" vertical>{{ loadingText }}</van-loading>
    </van-overlay>

  </div>
</template>

<script>
import { Toast } from "vant";
import { isTaikangSalesmanMode } from '@/utils/sanGaoSalesmanMode';
import {
  getSanGaoOrgById,
  sendSanGaoCode,
  registerSanGaoSalesman,
  loginSanGaoSalesman,
} from "@/api/sanGao";

const STORAGE_KEYS = {
  token: "san_gao_salesman_token",
  info: "san_gao_salesman_info",
  org: "san_gao_org_info",
  orgId: "san_gao_salesman_orgId",
  mode: "san_gao_salesman_mode",
};

const PHONE_REGEX = /^1[3-9]\d{9}$/;
const CODE_REGEX = /^\d{6}$/;

export default {
  name: "SanGaoSalesmanLogin",
  data() {
    return {
      orgId: null,
      orgInfo: null,
      loadingOrg: true,
      orgValid: false,
      activeTab: 0,
      registerForm: {
        salesmanName: "",
        salesmanCode: "",
        userPhone: "",
        securityCode: "",
      },
      loginForm: {
        userPhone: "",
        securityCode: "",
        salesmanCode: "",
      },
      countdown: 0,
      loginCountdown: 0,
      submitting: false,
      loadingText: "处理中...",
    };
  },
  computed: {
    taikangMode() { return isTaikangSalesmanMode(this.$route.query); },
    salesmanCodeInvalid() {
      return (
        this.registerForm.salesmanCode.length > 0 &&
        (this.registerForm.salesmanCode.trim().length === 0 || this.registerForm.salesmanCode.length > 32)
      );
    },
    canGetCode() {
      return !this.taikangMode && PHONE_REGEX.test(this.registerForm.userPhone) && this.countdown === 0;
    },
    codeButtonText() {
      return this.countdown > 0 ? `${this.countdown}s 后重发` : "获取验证码";
    },
    canRegister() {
      return (
        this.orgValid &&
        this.registerForm.salesmanName.trim().length > 0 &&
        this.registerForm.salesmanCode.trim().length > 0 &&
        !this.salesmanCodeInvalid &&
        PHONE_REGEX.test(this.registerForm.userPhone) &&
        (this.taikangMode || CODE_REGEX.test(this.registerForm.securityCode))
      );
    },
    canGetLoginCode() {
      return !this.taikangMode && PHONE_REGEX.test(this.loginForm.userPhone) && this.loginCountdown === 0;
    },
    loginCodeButtonText() {
      return this.loginCountdown > 0 ? `${this.loginCountdown}s 后重发` : "获取验证码";
    },
    canLogin() {
      return (
        PHONE_REGEX.test(this.loginForm.userPhone) &&
        (this.taikangMode
          ? this.orgValid && this.loginForm.salesmanCode.trim().length > 0 && this.loginForm.salesmanCode.length <= 32
          : CODE_REGEX.test(this.loginForm.securityCode))
      );
    },
  },
  watch: {
    taikangMode() { this.registerForm.securityCode = ''; this.loginForm.securityCode = ''; },
    '$route.query.orgId'(orgId) {
      this.orgId = orgId ? String(orgId) : null;
      this.orgInfo = null;
      this.orgValid = false;
      if (this.orgId) this.fetchOrg();
      else { this.loadingOrg = false; this.activeTab = 1; }
    },
  },
  created() {
    const orgId = this.$route.query.orgId;
    if (!orgId) {
      // 公众号底部菜单进入时无机构信息：无 orgId 不能注册，只展示登录
      this.loadingOrg = false;
      this.orgValid = false;
      this.activeTab = 1;
      return;
    }
    // type 参数区分入口：type=login 进登录页，type=register 进注册页
    const type = this.$route.query.type;
    if (type === "login") {
      this.activeTab = 1;
    } else if (type === "register") {
      this.activeTab = 0;
    }
    this.orgId = String(orgId);
    localStorage.setItem(STORAGE_KEYS.orgId, this.orgId);
    this.fetchOrg();
  },
  methods: {
    async fetchOrg() {
      const orgId = this.orgId;
      this.loadingOrg = true;
      try {
        const res = await getSanGaoOrgById({ orgId });
        if (this._isDestroyed || String(this.orgId) !== String(orgId)) return;
        if (res && res.code === 200 && res.data) {
          this.orgInfo = res.data;
          this.orgValid = true;
          localStorage.setItem(STORAGE_KEYS.org, JSON.stringify(res.data));
        } else {
          this.orgInfo = null;
          this.orgValid = false;
        }
      } catch (e) {
        if (this._isDestroyed || String(this.orgId) !== String(orgId)) return;
        this.orgInfo = null;
        this.orgValid = false;
        Toast("机构信息获取失败");
      } finally {
        if (String(this.orgId) === String(orgId)) this.loadingOrg = false;
      }
    },
    startCountdown(field) {
      const key = field === "login" ? "loginCountdown" : "countdown";
      this[key] = 60;
      const timer = setInterval(() => {
        this[key] -= 1;
        if (this[key] <= 0) {
          clearInterval(timer);
        }
      }, 1000);
    },
    async sendCode(phone) {
      if (this.taikangMode) return false;
      const res = await sendSanGaoCode({ userPhone: phone });
      if (res && res.code === 200) {
        Toast("验证码已发送");
        return true;
      }
      Toast((res && res.msg) || "发送失败，请重试");
      return false;
    },
    async handleGetCode() {
      if (!this.canGetCode) return;
      const ok = await this.sendCode(this.registerForm.userPhone);
      if (ok) {
        this.startCountdown("register");
      }
    },
    async handleGetLoginCode() {
      if (!this.canGetLoginCode) return;
      const ok = await this.sendCode(this.loginForm.userPhone);
      if (ok) {
        this.startCountdown("login");
      }
    },
    hasLoginSession(data) {
      return !!(
        data && typeof data.token === "string" && data.token.trim() &&
        data.salesman && data.salesman.id && data.salesman.orgId
      );
    },
    persistAuth(data) {
      if (!this.hasLoginSession(data)) {
        throw new Error("登录信息不完整，请重试");
      }
      const taikangMode = data.taikangMode === true;
      if (taikangMode !== this.taikangMode || (taikangMode && String(data.salesman.orgId) !== String(this.orgId))) {
        throw new Error('登录信息不匹配，请重新登录');
      }
      localStorage.setItem(STORAGE_KEYS.token, data.token.trim());
      localStorage.setItem(STORAGE_KEYS.mode, taikangMode ? 'taikang' : 'normal');
      localStorage.setItem(STORAGE_KEYS.info, JSON.stringify(data.salesman));
      this.orgId = String(data.salesman.orgId);
      localStorage.setItem(STORAGE_KEYS.orgId, this.orgId);
      if (data.organization) {
        localStorage.setItem(STORAGE_KEYS.org, JSON.stringify(data.organization));
      }
    },
    async handleRegister() {
      if (!this.canRegister || this.submitting) return;
      if (!this.orgValid) {
        Toast("机构id不正确，无法注册");
        return;
      }
      this.submitting = true;
      this.loadingText = "注册中...";
      const taikangMode = this.taikangMode;
      const requestOrgId = this.orgId;
      let registered = false;
      const payload = {
        orgId: Number(this.orgId),
        salesmanName: this.registerForm.salesmanName.trim(),
        salesmanCode: this.registerForm.salesmanCode,
        userPhone: this.registerForm.userPhone,
      };
      if (taikangMode) payload.taikangMode = true;
      else payload.securityCode = this.registerForm.securityCode;
      try {
        const res = await registerSanGaoSalesman(payload);
        if (this._isDestroyed || taikangMode !== this.taikangMode || String(this.orgId) !== String(requestOrgId)) return;
        if (res && res.code === 200) {
          registered = true;
          let session = res.data;
          // 注册接口缺少登录态时，按对应入口复用注册资料自动登录。
          if (!this.hasLoginSession(session)) {
            this.loadingText = "注册成功，正在登录...";
            const loginRes = await loginSanGaoSalesman(taikangMode ? {
              orgId: payload.orgId,
              userPhone: payload.userPhone,
              salesmanCode: payload.salesmanCode,
              taikangMode: true,
            } : {
              orgId: payload.orgId,
              userPhone: payload.userPhone,
              securityCode: payload.securityCode,
            });
            if (this._isDestroyed || taikangMode !== this.taikangMode || String(this.orgId) !== String(requestOrgId)) return;
            if (!loginRes || loginRes.code !== 200) {
              throw new Error((loginRes && loginRes.msg) || "自动登录失败");
            }
            session = loginRes.data;
          }
          this.persistAuth(session);
          Toast("注册成功，已自动登录");
          this.goToIndex();
        } else {
          Toast((res && res.msg) || "注册失败");
        }
      } catch (e) {
        if (this._isDestroyed || taikangMode !== this.taikangMode || String(this.orgId) !== String(requestOrgId)) return;
        if (registered) {
          // 注册已完成时保留输入供登录重试，避免再次注册或要求重新填写资料。
          this.loginForm.userPhone = payload.userPhone;
          this.loginForm.securityCode = payload.securityCode || '';
          this.loginForm.salesmanCode = payload.salesmanCode;
          this.activeTab = 1;
          Toast(`注册已成功，${e.message || "自动登录暂未完成"}，请点击登录重试`);
        } else {
          Toast("注册失败，请重试");
        }
      } finally {
        this.submitting = false;
      }
    },
    async handleLogin() {
      if (!this.canLogin || this.submitting) return;
      this.submitting = true;
      this.loadingText = "登录中...";
      const taikangMode = this.taikangMode;
      const requestOrgId = this.orgId;
      try {
        const payload = taikangMode ? {
          userPhone: this.loginForm.userPhone,
          salesmanCode: this.loginForm.salesmanCode,
          taikangMode: true,
        } : {
          userPhone: this.loginForm.userPhone,
          securityCode: this.loginForm.securityCode,
        };
        if (this.orgId) {
          payload.orgId = Number(this.orgId);
        }
        const res = await loginSanGaoSalesman(payload);
        if (this._isDestroyed || taikangMode !== this.taikangMode || String(this.orgId) !== String(requestOrgId)) return;
        if (res && res.code === 200 && res.data) {
          this.persistAuth(res.data);
          Toast("登录成功");
          this.goToIndex();
        } else {
          Toast((res && res.msg) || "登录失败");
        }
      } catch (e) {
        Toast("登录失败，请重试");
      } finally {
        this.submitting = false;
      }
    },
    goToIndex() {
      const query = {};
      if (this.taikangMode) query.taikang = '1';
      if (this.orgId) {
        query.orgId = this.orgId;
      }
      this.$router.replace({ path: "/sanGao/salesman/index", query });
    },
    switchToLogin() {
      this.activeTab = 1;
    },
    switchToRegister() {
      this.activeTab = 0;
    },
  },
};
</script>

<style lang="less" scoped>
* {
  box-sizing: border-box;
}

button {
  font: inherit;
}

.salesman-page {
  position: relative;
  min-height: 100vh;
  padding: 24px 16px 32px;
  background: linear-gradient(180deg, #0874ca 0%, #4ba8e0 28%, #dff6ff 60%, #dff6ff 100%);
  color: #163b5c;
}

.org-banner {
  margin: 0 0 18px;
  padding: 18px 18px 16px;
  border: 1px solid rgba(255, 255, 255, 0.65);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 6px 18px rgba(12, 91, 147, 0.12);
  text-align: center;
}

.org-banner.invalid {
  border-color: rgba(232, 78, 78, 0.6);
  background: rgba(255, 245, 245, 0.96);
}

.org-banner-label {
  margin: 0 0 6px;
  font-size: 11px;
  letter-spacing: 2px;
  color: #6f8aa1;
}

.org-banner.invalid .org-banner-label {
  color: #d65a5a;
}

.org-banner-name {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.4;
  color: #163b5c;
}

.org-banner.invalid .org-banner-name {
  color: #c1392b;
}

.org-banner-id {
  margin: 6px 0 0;
  font-size: 12px;
  color: #6f8aa1;
}

.org-banner.invalid .org-banner-id {
  color: #a7605b;
}

.form-card {
  padding: 12px 14px 18px;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(12, 91, 147, 0.1);
}

/deep/ .van-tabs__nav--card {
  height: 36px;
}

/deep/ .van-tab {
  font-size: 14px;
}

/deep/ .van-cell {
  padding: 12px 6px;
  border-bottom: 1px solid #eef4f9;
}

/deep/ .van-cell.field-error {
  border-bottom: 1px solid #e25b5b;
}

/deep/ .van-cell.field-error .van-field__control {
  color: #d65454;
}

.field-error-tip {
  margin: 6px 6px 0;
  font-size: 11px;
  color: #d65454;
}

.field-count-tip {
  margin: 6px 6px 0;
  font-size: 12px;
  color: #7c8d9c;
}

.code-button {
  padding: 6px 12px;
  border: 0;
  border-radius: 18px;
  font-size: 13px;
  color: #168de0;
  background: rgba(22, 141, 224, 0.1);
  cursor: pointer;
}

.code-button:disabled {
  color: #b8c6d2;
  background: #f1f5f8;
  cursor: not-allowed;
}

.submit-button {
  display: block;
  width: 100%;
  height: 46px;
  margin-top: 22px;
  border: 0;
  border-radius: 23px;
  font-size: 15px;
  font-weight: 600;
  color: #fff;
  background: linear-gradient(100deg, #19a2e5, #146bd2);
  box-shadow: 0 6px 15px rgba(17, 112, 199, 0.22);
  cursor: pointer;
}

.submit-button:disabled {
  color: rgba(255, 255, 255, 0.85);
  background: #b4cad9;
  box-shadow: none;
  cursor: not-allowed;
}

.switch-tip {
  margin: 16px 0 4px;
  font-size: 12px;
  text-align: center;
  color: #7c8d9c;
}

.switch-tip a {
  color: #168de0;
  cursor: pointer;
}

/deep/ .van-loading {
  color: #fff;
}
</style>
